// Admin editor veřejného Fokus seznamu — výběr hráčů, poznámky, publikace.
import React, { useState } from "react";
import { toast } from "sonner";
import { X, Globe, FloppyDisk } from "@phosphor-icons/react";
import { focusListUpdate } from "../../lib/focus-api";

const VERDICTS = [
    ["buy", "BUY", "border-fa-mint text-fa-mint", "bg-fa-mint text-black border-fa-mint"],
    ["watch", "WATCH", "border-fa-yellow text-fa-yellow", "bg-fa-yellow text-black border-fa-yellow"],
    ["avoid", "AVOID", "border-red-400 text-red-300", "bg-red-400 text-black border-red-400"],
];

export function VerdictPicker({ value, onChange, fplId }) {
    return (
        <div className="flex gap-1" data-testid={`verdict-picker-${fplId}`}>
            {VERDICTS.map(([k, label, idle, act]) => (
                <button key={k} type="button" onClick={() => onChange(k)} data-testid={`verdict-${k}-${fplId}`}
                        className={`font-mono text-[8px] uppercase tracking-widest px-2 py-1 border font-bold transition-colors ${value === k ? act : `${idle} opacity-60 hover:opacity-100`}`}>
                    {label}
                </button>
            ))}
        </div>
    );
}

const inputCls = "bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[11px] w-full focus:outline-none focus:border-fa-mint placeholder:text-white/25";

export default function PublishFocusList({ list, onClose }) {
    const [doc, setDoc] = useState(list);
    const [busy, setBusy] = useState(false);

    const setPlayer = (fplId, patch) => setDoc({
        ...doc,
        players: doc.players.map((p) => (p.fpl_id === fplId ? { ...p, ...patch } : p)),
    });

    const save = async (extra = {}) => {
        setBusy(true);
        try {
            const d = await focusListUpdate(doc.id, {
                title: doc.title, intro: doc.intro,
                players: doc.players.map((p) => ({ fpl_id: p.fpl_id, note: p.note || null, included: p.included !== false, verdict: p.verdict || null })),
                ...extra,
            });
            setDoc(d);
            toast.success(extra.status === "published" ? `Publikováno — /fokus/${d.slug}` : extra.status === "draft" ? "Staženo z webu" : "Uloženo");
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
        finally { setBusy(false); }
    };

    const includedCount = doc.players.filter((p) => p.included !== false).length;

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto" data-testid="publish-focus-modal">
            <div className="max-w-4xl mx-auto my-6 border border-fa-yellow/40 bg-[#0d0d0d] p-4 md:p-6 space-y-4">
                <div className="flex items-center gap-2">
                    <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 bg-fa-yellow text-black font-bold">Fokus seznam pro čtenáře</span>
                    <span className={`font-mono text-[9px] uppercase tracking-widest ${doc.status === "published" ? "text-fa-mint" : "text-fa-yellow"}`} data-testid="publish-focus-status">
                        {doc.status === "published" ? "Publikováno" : "Koncept"}
                    </span>
                    {doc.status === "published" && (
                        <a href={`/fokus/${doc.slug}`} target="_blank" rel="noreferrer" className="font-mono text-[9px] text-fa-sky underline" data-testid="publish-focus-link">/fokus/{doc.slug}</a>
                    )}
                    <button type="button" onClick={onClose} className="ml-auto text-white/50 hover:text-white" data-testid="publish-focus-close"><X size={16} /></button>
                </div>
                <input value={doc.title || ""} onChange={(e) => setDoc({ ...doc, title: e.target.value })}
                       placeholder="Titulek seznamu" className={`${inputCls} font-display font-bold text-base`} data-testid="publish-focus-title" />
                <textarea value={doc.intro || ""} onChange={(e) => setDoc({ ...doc, intro: e.target.value })} rows={2}
                          placeholder="Úvod pro čtenáře — proč tento seznam a jak vznikl" className={inputCls} data-testid="publish-focus-intro" />
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Hráči ({includedCount} zveřejněných z {doc.players.length})</p>
                <div className="space-y-2">
                    {doc.players.map((p) => (
                        <div key={p.fpl_id} className={`border p-2.5 ${p.included !== false ? "border-white/15 bg-black/40" : "border-white/5 bg-black/20 opacity-50"}`} data-testid={`publish-focus-player-${p.fpl_id}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                                <input type="checkbox" checked={p.included !== false}
                                       onChange={(e) => setPlayer(p.fpl_id, { included: e.target.checked })}
                                       className="accent-[#98FB98]" data-testid={`publish-focus-include-${p.fpl_id}`} />
                                <span className="font-display font-bold text-sm">{p.name}</span>
                                <span className="font-mono text-[9px] text-white/50">{p.team} · {p.position} · £{p.price}</span>
                                {p.candidate_status && <span className="font-mono text-[8px] uppercase px-1.5 py-0.5 border border-fa-sky/40 text-fa-sky">{p.candidate_status}</span>}
                                <span className="ml-auto font-mono text-[9px] text-white/45">
                                    {p.predicted_total != null && <>model {p.predicted_total} b/GW{p.pred_window} · </>}
                                    own {p.ownership_pct}%
                                    {p.scouting?.trend && <> · trend {p.scouting.trend}</>}
                                    {p.scouting?.preseason_rating != null && <> · scout {p.scouting.preseason_rating}/10</>}
                                </span>
                            </div>
                            {p.included !== false && (
                                <div className="mt-2 space-y-1.5">
                                    <VerdictPicker value={p.verdict} onChange={(v) => setPlayer(p.fpl_id, { verdict: v })} fplId={p.fpl_id} />
                                    <input value={p.note || ""} onChange={(e) => setPlayer(p.fpl_id, { note: e.target.value })}
                                           placeholder="Komentář pro čtenáře (proč je na seznamu)…"
                                           className={inputCls} data-testid={`publish-focus-note-${p.fpl_id}`} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button type="button" onClick={() => save()} disabled={busy} data-testid="publish-focus-save-btn"
                            className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-fa-mint/60 text-fa-mint hover:bg-fa-mint hover:text-black disabled:opacity-40">
                        <FloppyDisk size={11} className="inline mb-0.5 mr-1" /> Uložit
                    </button>
                    {doc.status !== "published" ? (
                        <button type="button" onClick={() => save({ status: "published" })} disabled={busy || !includedCount} data-testid="publish-focus-publish-btn"
                                className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 bg-fa-mint text-black font-bold disabled:opacity-40">
                            <Globe size={11} className="inline mb-0.5 mr-1" /> Publikovat
                        </button>
                    ) : (
                        <button type="button" onClick={() => save({ status: "draft" })} disabled={busy} data-testid="publish-focus-unpublish-btn"
                                className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-fa-yellow/60 text-fa-yellow hover:bg-fa-yellow hover:text-black disabled:opacity-40">
                            Stáhnout z webu
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
